import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Button, FormControl, Heading, TextInput } from '@primer/react';
import type { User } from 'firebase/auth';

import type { ConsoleConfig } from '../store.js';
import { initializeConsoleFirebase, observeConsoleUser, signInConsole, signOutConsole } from '../firebase-auth.js';

export function FirebaseAuthGate({ config, children }: { config: ConsoleConfig; children: ReactNode }): ReactNode {
  const [user, setUser] = useState<User | null>(null);
  const [ready, setReady] = useState(config.firebase === null);
  const [denied, setDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [retry, setRetry] = useState(0);

  useEffect(() => {
    if (config.firebase === null) return;
    initializeConsoleFirebase(config.firebase);
    let current = true;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    const unsubscribe = observeConsoleUser((next) => {
      if (retryTimer !== undefined) clearTimeout(retryTimer);
      setDenied(false);
      setError(null);
      if (next === null) { setUser(null); setReady(true); return; }
      setUser((admitted) => {
        if (admitted?.uid !== next.uid) setReady(false);
        return admitted?.uid === next.uid ? admitted : null;
      });

      const verify = async (): Promise<void> => {
        try {
          const token = await next.getIdToken();
          const response = await fetch(`${config.baseUrl}/v1/health`, {
            headers: { Authorization: `Bearer ${token}` },
            credentials: 'same-origin',
          });
          if (!current) return;
          if (response.ok) {
            setUser(next);
            setDenied(false);
            setError(null);
            setReady(true);
            return;
          }
          if (response.status === 401 || response.status === 403) {
            setUser(null);
            setDenied(true);
            setError(null);
            setReady(true);
            return;
          }
          throw new Error(`Control plane returned ${response.status}`);
        } catch {
          if (!current) return;
          setDenied(false);
          setError('Не удалось проверить сессию. Повторяем подключение…');
          setReady(true);
          retryTimer = setTimeout(() => void verify(), 2_000);
        }
      };
      void verify();
    });
    return () => { current = false; if (retryTimer !== undefined) clearTimeout(retryTimer); unsubscribe(); };
  }, [config, retry]);

  if (config.firebase === null || user !== null) return children;
  if (!ready) return <main className="auth-page"><div className="auth-card">Checking your session…</div></main>;
  if (denied) return <main className="auth-page"><div className="auth-card">
    <Heading as="h1">Нет доступа</Heading>
    <p className="auth-copy">Этот Firebase-пользователь не добавлен в команду.</p>
    <Button onClick={() => void signOutConsole()}>Войти другим пользователем</Button>
  </div></main>;
  if (error !== null) return <main className="auth-page"><div className="auth-card">
    <Heading as="h1">Связь временно недоступна</Heading>
    <p className="auth-copy" role="status">{error}</p>
    <Button variant="primary" onClick={() => setRetry((value) => value + 1)}>Повторить сейчас</Button>
    <Button onClick={() => void signOutConsole()}>Выйти</Button>
  </div></main>;

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    const fields = new FormData(event.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await signInConsole(String(fields.get('email') ?? '').trim(), String(fields.get('password') ?? ''));
    } catch {
      setError('Не удалось войти. Проверьте email, пароль и доступ к этой команде.');
    } finally {
      setBusy(false);
    }
  }

  return <main className="auth-page">
    <form className="auth-card" onSubmit={(event) => void submit(event)}>
      <div className="auth-mark" aria-hidden="true">◉</div>
      <Heading as="h1">Autonomous Agents</Heading>
      <p className="auth-copy">Войдите, чтобы управлять автономной командой.</p>
      <FormControl required><FormControl.Label>Email</FormControl.Label><TextInput name="email" type="email" autoComplete="email" block autoFocus /></FormControl>
      <FormControl required><FormControl.Label>Пароль</FormControl.Label><TextInput name="password" type="password" autoComplete="current-password" block /></FormControl>
      {error === null ? null : <p className="auth-error" role="alert">{error}</p>}
      <Button type="submit" variant="primary" block disabled={busy}>{busy ? 'Входим…' : 'Войти'}</Button>
    </form>
  </main>;
}
